import React, { useEffect, useRef, useState } from 'react';

export default function ControlledInvoiceRecipientApproval({ token }) {
  const [input, setInput] = useState({ provider: 'seal', request_id: '', shopify_customer_id: '', seal_subscription_id: '', stripe_subscription_id: '' });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const activeRequest = useRef(null);
  useEffect(() => () => { const pending = activeRequest.current; activeRequest.current = null; pending?.abort(); }, []);
  useEffect(() => {
    const pending = activeRequest.current; activeRequest.current = null; pending?.abort();
    setPreview(null); setMessage(''); setBusy(false);
  }, [token]);
  async function submit(action) {
    if (busy || activeRequest.current) return;
    const controller = new AbortController(); activeRequest.current = controller;
    setBusy(true); setMessage('');
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/admin/controlled-invoices/recipient/${action}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, signal: controller.signal,
        body: JSON.stringify(action === 'preview' ? input : {
          provider: input.provider, preview_token: preview.preview_token, confirmation: 'APPROVE_INVOICE_RECIPIENT_ONLY',
        }),
      });
      if (!response.ok) throw new Error('Approval unavailable');
      const data = await response.json();
      if (controller.signal.aborted || activeRequest.current !== controller) return;
      if (action === 'preview') {
        if (!data.preview_token || !data.recipient || data.activation !== false || data.writes !== 0) throw new Error('Invalid preview');
        setPreview(data);
      } else {
        if (!data.profile_id || data.activation !== false || data.customer_mail_sent !== false) throw new Error('Invalid acknowledgement');
        setPreview(null); setMessage('Rechnungsempfänger gespeichert. Keine Automatik aktiviert und keine Kundenmail versendet.');
      }
    } catch (_) {
      if (activeRequest.current === controller) {
        setPreview(null);
        setMessage(action === 'preview'
          ? 'Vorschau nicht verfügbar. Zuordnung und vorbereitete Umstellung prüfen; anschließend erneut versuchen.'
          : 'Das Freigabeergebnis ist unklar. Bitte eine neue Vorschau laden; eine identische Freigabe erzeugt keine zweite Version.');
      }
    } finally {
      clearTimeout(timeout);
      if (activeRequest.current === controller) { activeRequest.current = null; setBusy(false); }
    }
  }
  const fields = input.provider === 'stripe'
    ? [['request_id', 'Nummer der Adressanfrage'], ['stripe_subscription_id', 'Stripe-Vertragskennung']]
    : [['request_id', 'Nummer der Adressanfrage'], ['shopify_customer_id', 'Shopify-Kundennummer'], ['seal_subscription_id', 'Seal-Vertragskennung']];
  const valid = key => key === 'stripe_subscription_id' ? /^sub_[A-Za-z0-9]+$/.test(input[key]) : /^\d+$/.test(input[key]);
  return <section aria-label="Rechnungsempfänger freigeben">
    <h4>Rechnungsempfänger pro Vertrag freigeben</h4>
    <p>Nur für bereits vorbereitete Umstellungen. Die Freigabe speichert die geprüfte Adresse; sie aktiviert weder Rechnungen noch Zahlungen oder E-Mails.</p>
    <form onSubmit={event => { event.preventDefault(); submit('preview'); }}>
      <label>Zahlungsanbieter <select value={input.provider} disabled={busy}
        onChange={event => { setInput({ ...input, provider: event.target.value }); setPreview(null); setMessage(''); }}>
        <option value="seal">Shopify / Seal</option><option value="stripe">Stripe</option>
      </select></label>
      {fields.map(([key, label]) => <label key={key} style={{ display: 'block', marginBottom: 8 }}>
        {label}<input value={input[key]} inputMode={key === 'stripe_subscription_id' ? 'text' : 'numeric'}
          pattern={key === 'stripe_subscription_id' ? 'sub_[A-Za-z0-9]+' : '[0-9]+'} required disabled={busy}
          onChange={event => { setInput({ ...input, [key]: event.target.value }); setPreview(null); setMessage(''); }} />
      </label>)}
      <button type="submit" disabled={busy || fields.some(([key]) => !valid(key))}>Empfänger-Vorschau laden</button>
    </form>
    {preview && <div>
      <p>{preview.recipient.company || preview.recipient.name}<br />{preview.recipient.street}<br />
        {preview.recipient.address_extra && <>{preview.recipient.address_extra}<br /></>}
        {preview.recipient.postal_code} {preview.recipient.city}<br />{preview.recipient.country}<br />{preview.recipient.email}</p>
      <p>Vertrag: {input.provider === 'stripe' ? input.stripe_subscription_id : input.seal_subscription_id} · Anfrage: {preview.request_id} · Intervall: {preview.billing_interval}</p>
      <button type="button" disabled={busy} onClick={() => submit('approve')}>Nur diesen Rechnungsempfänger freigeben</button>
    </div>}
    {busy && <p role="status">Wird geprüft …</p>}
    {message && <p role="status">{message}</p>}
  </section>;
}
